import React, { useEffect, useMemo, useRef, useState } from 'react';

export interface ChipOption {
    value: number | string;
    label: string;
}

interface Props {
    options: ChipOption[];
    selected: ChipOption[];
    onChange: (next: ChipOption[]) => void;
    placeholder: string;
    noResultsMessage?: React.ReactNode;
    emptyHint?: React.ReactNode;
    ariaLabel?: string;
    /** Visual variant — `mat` (materials) uses brand-soft tokens, `default` (equipment) uses navy-800. */
    variant?: 'mat' | 'default';
}

// Matches .art-chip-select__dropdown max-height in articles.scss.
const DROPDOWN_MAX_HEIGHT = 290;

const ChipMultiSelect: React.FC<Props> = ({
    options,
    selected,
    onChange,
    placeholder,
    noResultsMessage,
    emptyHint,
    ariaLabel,
    variant = 'default',
}) => {
    const [open, setOpen] = useState(false);
    const [placement, setPlacement] = useState<'top' | 'bottom'>('bottom');
    const [search, setSearch] = useState('');
    const containerRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const inputWrapRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!open) return;
        const handler = (e: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
                setOpen(false);
                setSearch('');
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [open]);

    // Open with viewport-aware placement so the panel doesn't get clipped by the
    // viewport bottom. Flips upward when more space exists above.
    const openDropdown = () => {
        if (inputWrapRef.current) {
            const rect = inputWrapRef.current.getBoundingClientRect();
            const spaceBelow = window.innerHeight - rect.bottom;
            const spaceAbove = rect.top;
            setPlacement(spaceBelow < DROPDOWN_MAX_HEIGHT && spaceAbove > spaceBelow ? 'top' : 'bottom');
        }
        setOpen(true);
    };

    const selectedKeys = useMemo(() => new Set(selected.map((s) => s.value)), [selected]);

    const add = (opt: ChipOption) => {
        if (!selectedKeys.has(opt.value)) onChange([...selected, opt]);
        setSearch('');
    };

    const remove = (opt: ChipOption) => {
        onChange(selected.filter((s) => s.value !== opt.value));
    };

    // Dropdown shows only AVAILABLE options (not already selected) filtered by search.
    // Matches the design's TokenPicker: clicking adds, the row disappears from the list.
    const available = useMemo(() => {
        const term = search.trim().toLowerCase();
        return options.filter((o) =>
            !selectedKeys.has(o.value) &&
            (!term || o.label.toLowerCase().includes(term)),
        );
    }, [options, selectedKeys, search]);

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Backspace' && search === '' && selected.length > 0) {
            e.preventDefault();
            remove(selected[selected.length - 1]);
        } else if (e.key === 'Escape') {
            setOpen(false);
            setSearch('');
        }
    };

    const chipClass = `art-chip art-chip--${variant}`;

    return (
        <div className="art-chip-select" ref={containerRef}>
            {selected.length > 0 ? (
                <div className="art-chip-select__chips">
                    {selected.map((opt) => (
                        <span key={opt.value} className={chipClass}>
                            <span className="art-chip__label">{opt.label}</span>
                            <button
                                type="button"
                                className="art-chip__remove"
                                aria-label={`Remove ${opt.label}`}
                                onClick={(e) => { e.stopPropagation(); remove(opt); }}
                            >
                                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                    <line x1="18" y1="6" x2="6" y2="18" />
                                    <line x1="6" y1="6" x2="18" y2="18" />
                                </svg>
                            </button>
                        </span>
                    ))}
                </div>
            ) : (
                emptyHint && <p className="art-chip-select__hint">{emptyHint}</p>
            )}

            <div className="art-chip-select__input-wrap" ref={inputWrapRef}>
                <svg className="art-chip-select__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <circle cx="11" cy="11" r="8" />
                    <path d="m21 21-4.3-4.3" />
                </svg>
                <input
                    ref={inputRef}
                    type="text"
                    className="art-chip-select__input"
                    placeholder={placeholder}
                    value={search}
                    onFocus={() => { if (!open) openDropdown(); }}
                    onChange={(e) => { setSearch(e.target.value); if (!open) openDropdown(); }}
                    onKeyDown={handleKeyDown}
                    aria-label={ariaLabel || placeholder}
                    aria-expanded={open}
                />
                <button
                    type="button"
                    className={`art-chip-select__toggle ${open ? 'is-open' : ''}`}
                    aria-label={placeholder}
                    onClick={() => { if (open) { setOpen(false); } else { openDropdown(); } inputRef.current?.focus(); }}
                >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="6 9 12 15 18 9" />
                    </svg>
                </button>
            </div>

            {open && (
                <div className={`art-chip-select__dropdown art-chip-select__dropdown--${placement}`} role="listbox">
                    {available.length === 0 ? (
                        <div className="art-chip-select__no-results">
                            {noResultsMessage || '—'}
                        </div>
                    ) : (
                        available.map((opt) => (
                            <button
                                type="button"
                                key={opt.value}
                                role="option"
                                aria-selected={false}
                                className="art-chip-select__option"
                                onClick={() => add(opt)}
                            >
                                <span className="art-chip-select__option-label">{opt.label}</span>
                                <span className="art-chip-select__option-plus" aria-hidden="true">
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M12 5v14M5 12h14" />
                                    </svg>
                                </span>
                            </button>
                        ))
                    )}
                </div>
            )}
        </div>
    );
};

export default ChipMultiSelect;
